import axios, {
  AxiosError,
  AxiosInstance,
  AxiosRequestConfig,
} from "axios";
import { appConfig } from "../appConfig";

const LOGIN_URL = appConfig.loginUrl;
const AUTH_TOKEN_KEY = "auth_token";

type RetryableConfig = AxiosRequestConfig & {
  _retry?: boolean;
  _authTokenUsed?: string | null;
  _usedCookieAuth?: boolean;
};

function getStoredToken(): string | null {
  return localStorage.getItem(AUTH_TOKEN_KEY);
}

const getLogoutUrlTail = (agreementId: string) => {
    switch (agreementId) {
        case "8356308c-818e-d5e6-b082-f25ebd1eac91":
            return "acr_values=urn:sb1finans:oidc:method:sbm"
        case "86A44569-2776-15D2-4832-C14F7AEBB0B7":
            return "acr_values=urn:sb1finans:oidc:method:bnbank"
        default:
            return `acr_values=urn:sb1finans:oidc:method:bankid`
    }
}

function redirectToLogin() {
    const agreementId = window.location.pathname.split("/").filter(Boolean)[0];
    const logoutUrlTail = getLogoutUrlTail(agreementId)
    const logoutUrl = `${LOGIN_URL}/login?redirectUri=${window.location.origin}/${agreementId}&${logoutUrlTail}`
    window.location.href = logoutUrl
}

function getBearerTokenFromRequest(
  config: AxiosRequestConfig
): string | null {
  const authHeader =
    (config.headers?.Authorization as string | undefined) ??
    (config.headers?.authorization as string | undefined);

  if (!authHeader) return null;

  const match = authHeader.match(/^Bearer\s+(.+)$/i);
  return match ? match[1] : null;
}

export function setupAxiosInterceptors(client: AxiosInstance) {
  client.interceptors.request.use((config: RetryableConfig) => {
    const token = getStoredToken();

    if (token) {
      config.headers = config.headers ?? {};
      config.headers.Authorization = `Bearer ${token}`;
      config._authTokenUsed = token;
      config._usedCookieAuth = false;
    } else {
      config.withCredentials = true;
      config._authTokenUsed = null;
      config._usedCookieAuth = true;
    }

    return config;
  });

  client.interceptors.response.use(
    (response) => {
      return response;
    },
    async (error: AxiosError) => {
      const response = error.response;
      const originalRequest = error.config as RetryableConfig | undefined;

      if (!response || !originalRequest) {
        return Promise.reject(error);
      }

      if (response.status !== 401) {
        return Promise.reject(error);
      }

      const requestToken =
        originalRequest._authTokenUsed ?? getBearerTokenFromRequest(originalRequest);

      // Case 1:
      // Unauthorized response and request contained a token
      if (requestToken) {
        const latestStoredToken = getStoredToken();

        // Avoid infinite retry loops
        if (!originalRequest._retry && latestStoredToken && latestStoredToken !== requestToken) {
          originalRequest._retry = true;
          originalRequest.headers = originalRequest.headers ?? {};
          originalRequest.headers.Authorization = `Bearer ${latestStoredToken}`;
          originalRequest._authTokenUsed = latestStoredToken;

          return client(originalRequest);
        }

        // Stored token is same as request token, or no token exists anymore
        redirectToLogin();
        return Promise.reject(error);
      }

      // Case 2:
      // Unauthorized response and cookie auth was used
      if (originalRequest._usedCookieAuth) {
        redirectToLogin();
      }

      return Promise.reject(error);
    }
  );
}